import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axiosDotnet from '../services/axiosDotnet';   
import useAuth from '../Hooks/auth/useAuth';
import { forgeAssistant } from '../services/groqService';
import { showSuccess, showWarning } from '../services/reactToastify';

export default function CreateWorkspace() {
  const navigate = useNavigate();
  const { auth } = useAuth();
  const [workspaceName, setWorkspaceName] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!workspaceName.trim()) {
      setError('Workspace name is required');
      showWarning('Workspace name is required')
      return;
    }

    if (!auth?.foundUser?.id) {
      setError('User not authenticated. Please login again.');
      return;
    }

    setError('');
    setLoading(true);

    try {
      // console.log(workspaceName, description)

      const response = await axiosDotnet.post('/api/workspaces', {
        UserId: auth?.foundUser?.id,
        Name: workspaceName.trim(),
        Description: description.trim() || null
      });

      console.log('Workspace created:', response.data);

      showSuccess('Workspace Created Successfully!');
      navigate('/dashboard');
    }
    catch (err) {
      console.error(err?.response);
      setError(
        err?.response?.data?.message ||
        err?.response?.data ||
        'Failed to create workspace. Please try again.'
      );
    }
    finally {
      setLoading(false);
    }
  };

  // ================= AI Integrate ===============>

  const [prompt, setPrompt] = useState('');
  const [response, setResponse] = useState('');

  useEffect(() => {

    const projectPromptTemplate = `
  You are an expert Company Workspace Manager and Technical Innovative Entrepreneur.
  
  Task:
  Create **ONE unique, specific, and actionable Community Workspace like a Company** .
  
  Important Rules:
  - Do NOT repeat or copy ideas from previous responses.
  - Make the Workspace name and description **more specific and focused**.
  - Be creative and specific.
  - The Workspace should be different every time this prompt is called.
  - Think of a **concrete initiative** or deliverable .
  
  Respond **only** with valid JSON in this exact format. No extra text, no explanations, no markdown.
  
  {
    "Name": "Unique and specific Company Workspace Name",
    "Description": "Detailed, focused Company Workspace description (2-3 sentences)",
  }
  `;

    const randomSeed = Math.random().toString(36).substring(2, 10);

    const finalPrompt = projectPromptTemplate + `\n\nGenerate a completely new idea. Random seed: ${randomSeed}`

    setPrompt(finalPrompt);

  }, [])
  // console.log(prompt)
  const handleAsk = async () => {
    if (!prompt.trim()) return;

    setLoading(true);
    try {
      const result = await forgeAssistant(prompt);

      // console.log("Raw response from Groq:", result);
      let parsedData;

      if (typeof result === 'object' && result !== null) {
        parsedData = result;
      }
      else if (typeof result === 'string') {
        let cleanResult = result
          .replace(/```json\n?/g, '')
          .replace(/```\n?/g, '')
          .trim();

        parsedData = JSON.parse(cleanResult);
      }

      setResponse(parsedData);
      // console.log("Successfully parsed JSON:", parsedData);
      setWorkspaceName(parsedData?.Name)
      setDescription(parsedData?.Description)
    } catch (err) {
      setResponse("Sorry, AI is currently unavailable.");
    } finally {
      setLoading(false);
    }
  }


  return (
    <div>
    <Link to={`/dashboard`}>
        <button className="mt-5 ml-5 cursor-pointer px-8 py-2 bg-zinc-800 hover:bg-zinc-700 rounded-2xl font-xxl">
          ←
        </button>
      </Link>
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center px-6 py-14">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-8">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-violet-600 rounded-2xl flex items-center justify-center text-white text-3xl font-bold">A</div>
            <span className="text-3xl font-semibold tracking-tight text-white">AetherForge</span>
          </div>
        </div>

        <div className="bg-zinc-900 border border-zinc-800 rounded-3xl p-10 shadow-2xl">
          <h2 className="text-3xl font-semibold text-center text-white mb-2">
            Create New Workspace
          </h2>
          <p className="text-zinc-400 text-center mb-8">
            A dedicated space for your team to collaborate
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">
                Workspace Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={workspaceName}
                onChange={(e) => setWorkspaceName(e.target.value)}
                className="w-full text-white bg-zinc-950 border border-zinc-700 focus:border-violet-500 rounded-2xl px-5 py-4 outline-none transition-colors"
                placeholder="e.g. Product Team"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">
                Description (optional)
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                className="w-full text-white bg-zinc-950 border border-zinc-700 focus:border-violet-500 rounded-2xl px-5 py-4 outline-none transition-colors resize-none"
                placeholder="What will this workspace be used for?"
              />
            </div>

            {error && (
              <p className="text-red-500 text-sm text-center bg-red-950/50 border border-red-900 rounded-2xl p-3">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={loading || !workspaceName.trim()}
              className="w-full cursor-pointer bg-violet-600 hover:bg-violet-500 disabled:bg-violet-800 py-4 rounded-2xl font-semibold text-lg transition-all"
            >
              {loading ? 'Creating Workspace...' : 'Create Workspace'}
            </button>
          </form>

          <button
            onClick={handleAsk}
            disabled={loading}
            className="w-full mt-5 cursor-pointer bg-violet-600 hover:bg-violet-500 disabled:bg-violet-800 py-4 rounded-2xl font-semibold text-lg transition-all"
          >
            AI Workspace Suggestions
          </button>

          <p className="text-center text-zinc-400 mt-8">
            Want to join an existing workspace?{' '}
            <Link to='/dashboard'>
              <button
                onClick={() => navigate('/workspaces')}
                className=" cursor-pointer text-violet-400 hover:text-violet-300 font-medium"
              >
                Browse Workspaces
              </button>
            </Link>
          </p>
        </div>
      </div>
    </div>
    </div>
  );
}
